<template>
    <div :class="`non-textual-diff ${diffFormat}`">
        <div v-if="diffFormat === 'side-by-side'" class="diff-item">
            <div class="diff-label">{{ fromName }}</div>
            <div class="diff-content">
                <img
                    v-if="contentFlavor === ContentFlavor.IMAGE"
                    :src="fromDataUrl"
                    class="image-preview"
                />
                <div
                    v-else-if="contentFlavor === ContentFlavor.PDF"
                    class="pdf-container"
                >
                    <object
                        :data="fromDataUrl"
                        :type="mimeType"
                        class="pdf-preview"
                    >
                        <div class="fallback">
                            <p>
                                <small
                                    >Your browser doesn't support PDF
                                    preview.</small
                                >
                            </p>
                        </div>
                    </object>
                </div>
                <div v-else class="unsupported">
                    <p>Cannot preview this file type: {{ mimeType }}</p>
                </div>
            </div>
        </div>
        <div class="diff-item">
            <div class="diff-label" v-if="diffFormat === 'side-by-side'">
                {{ toName }}
            </div>
            <div class="diff-content">
                <img
                    v-if="contentFlavor === ContentFlavor.IMAGE"
                    :src="toDataUrl"
                    class="image-preview"
                />
                <div
                    v-else-if="contentFlavor === ContentFlavor.PDF"
                    class="pdf-container"
                >
                    <object
                        :data="toDataUrl"
                        :type="mimeType"
                        class="pdf-preview"
                    >
                        <div class="fallback">
                            <p>
                                <small
                                    >Your browser doesn't support PDF
                                    preview.</small
                                >
                            </p>
                        </div>
                    </object>
                </div>
                <div v-else class="unsupported">
                    <p>Cannot preview this file type: {{ mimeType }}</p>
                </div>
            </div>
        </div>
    </div>
</template>

<script lang="ts" setup>
import { ContentFlavor, getMimetypeFlavor } from "@/lib/contentFlavor";
import { computed, onMounted, ref } from "vue";

const props = defineProps<{
    from: Blob;
    to: Blob;
    fromName: string;
    toName: string;
    mimeType: string;
    diffFormat?: "side-by-side" | "line-by-line";
}>();

const fromDataUrl = ref("");
const toDataUrl = ref("");

const contentFlavor = computed(() => {
    return getMimetypeFlavor(props.mimeType);
});

onMounted(async () => {
    // Create data URLs for the blobs
    fromDataUrl.value = URL.createObjectURL(props.from);
    toDataUrl.value = URL.createObjectURL(props.to);
});
</script>

<style lang="scss" scoped>
.non-textual-diff {
    display: flex;
    width: 100%;
    gap: 1rem;
    height: calc(100vh - 150px);

    &:not(.side-by-side) {
        .diff-item {
            width: 100%;
        }
    }
}

.diff-item {
    flex: 1;
    display: flex;
    flex-direction: column;
    border: 1px solid var(--stekz-light-gray, #e5e5e5);
    border-radius: 4px;
    overflow: hidden;
}

.diff-label {
    padding: 0.5rem;
    background-color: var(--stekz-light-gray, #f5f5f5);
    font-weight: 500;
    text-align: center;
    border-bottom: 1px solid var(--stekz-light-gray, #e5e5e5);
}

.diff-content {
    flex: 1;
    overflow: auto;
    display: flex;
    align-items: center;
    justify-content: center;
}

.image-preview {
    max-width: 100%;
    max-height: 100%;
    object-fit: contain;
}

.pdf-container {
    width: 100%;
    height: 100%;
}

.pdf-preview {
    width: 100%;
    height: 100%;
}

.unsupported {
    display: flex;
    align-items: center;
    justify-content: center;
    height: 100%;
    padding: 1rem;
    text-align: center;
}

.fallback {
    display: flex;
    align-items: center;
    justify-content: center;
    height: 100%;
    text-align: center;
}
</style>
